// Reduce LLM/agent-authored markdown to PLAIN TEXT for surfaces that render it
// with no markdown renderer (e.g. notification title/body, the goal ledger's
// rows), so raw syntax never leaks into the UI. Strips block syntax (thematic
// breaks, ATX headings, blockquotes, list bullets) and inline syntax
// (bold/italic/strike emphasis, links, images, inline code), collapsing
// everything to a single whitespace-normalized line.
//
// **This is the web third of a three-surface mirror** — iOS `markdownPlainText`
// (`Features/Chat/MarkdownPlainText.swift`) and Android `markdownToPlainText`
// (`features/MarkdownBlocks.kt`) are the same reduction, and the two phones had
// two rules this copy did not:
//
//   1. **emphasis is removed MARKER-wise, not pair-wise.** A real LLM turn
//      routinely emits an unclosed `**` ("**Top priorities needing attention:")
//      and a pair-matching regex leaves it on screen, which is the leak the
//      phones fixed and this one still had.
//   2. **`_` only goes where it reads as a delimiter — never between two
//      alphanumerics** — so `snake_case` and the agent's own tool names survive.
//
// Note: the Morning Brief headline keeps its own inline variant that PRESERVES
// `**` pairs because it actually renders the bold (see MaestroBrief) — this one
// is the fuller reduction for plain-text surfaces.
export function stripMarkdown(text: string): string {
  if (!text) return '';
  const flattened = text
    .split('\n')
    // Fence lines go; the code inside them is words and stays.
    .filter((line) => !/^\s*```/.test(line))
    // A whole-line thematic break (`---` / `***` / `___`) carries no words at all.
    .filter((line) => !/^\s*([-*_])\1{2,}\s*$/.test(line))
    .map((line) =>
      line
        .replace(/^\s*>+\s?/, '')
        .replace(/^\s*#{1,6}\s+/, '')
        .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, ''),
    )
    .join('\n')
    // Images and links keep their words and lose their targets.
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/`/g, '')
    .replace(/\*\*|__|~~/g, '')
    .replace(/\*/g, '');
  return removeBoundaryUnderscores(flattened).replace(/\s+/g, ' ').trim();
}

/** Drops `_` only where it reads as an emphasis delimiter — i.e. NOT between two alphanumerics — so
 *  `_italic_` loses its markers while `snake_case` / `file_name.txt` keep theirs. A character scan
 *  rather than a regex, so it is the same rule iOS `removeBoundaryUnderscores` implements. */
function removeBoundaryUnderscores(value: string): string {
  if (!value.includes('_')) return value;
  const isWordChar = (char: string | undefined): boolean => !!char && /[\p{L}\p{N}]/u.test(char);
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (char !== '_') {
      out += char;
      continue;
    }
    if (isWordChar(value[i - 1]) && isWordChar(value[i + 1])) out += char;
  }
  return out;
}
